import { normalizeDetailResponse } from '@/utils/apiResponseUtils'
import type { PagedListResult } from '@/utils/apiResponseUtils'
import type { NhomHangCayNode, NhomHangId } from '@/interfaces/nhomHangInterfaces'
import type { TestDataSeedOptions, TestDataSeedProgress } from '@/modules/test-data/testDataSeed.types'

export const stamp = () => Date.now().toString(36).slice(-6)

export const pad2 = (n: number) => String(n).padStart(2, '0')

export const pad3 = (n: number) => String(n).padStart(3, '0')

export const extractEntityId = (res: unknown): string => {
  const detail = normalizeDetailResponse<Record<string, unknown>>(
    res as { data?: Record<string, unknown> },
  )
  return String(detail.id ?? '')
}

export const errorMessage = (e: unknown, fallback: string): string =>
  (e as { message?: string })?.message ?? fallback

export const isDuplicateEntityError = (e: unknown): boolean => {
  const msg = errorMessage(e, '').toLowerCase()
  return msg.includes('đã tồn tại') || msg.includes('already exist') || msg.includes('duplicate')
}

export const normKey = (v: string | null | undefined): string => v?.trim().toLowerCase() ?? ''

export const buildMaSet = (rows: Array<{ ma?: string | null; maKhachHang?: string | null; maNhaCungCap?: string | null; maThuongHieu?: string | null; maDonViTinh?: string | null; maViTri?: string | null; code?: string | null }>): Set<string> => {
  const set = new Set<string>()
  for (const row of rows) {
    const ma =
      row.ma ??
      row.maKhachHang ??
      row.maNhaCungCap ??
      row.maThuongHieu ??
      row.maDonViTinh ??
      row.maViTri ??
      row.code
    const key = normKey(ma != null ? String(ma) : '')
    if (key) set.add(key)
  }
  return set
}

export const buildTenSet = (rows: Array<{ tenNhom?: string | null; ten?: string | null; name?: string | null }>): Set<string> => {
  const set = new Set<string>()
  for (const row of rows) {
    const ten = row.tenNhom ?? row.ten ?? row.name
    const key = normKey(ten != null ? String(ten) : '')
    if (key) set.add(key)
  }
  return set
}

/** API backend giới hạn pageSize tối đa 100. */
export const SEED_LIST_PAGE_SIZE = 100

export const fetchAllPaged = async <T>(
  fetchPage: (search: string) => Promise<PagedListResult<T>>,
  pageSize = SEED_LIST_PAGE_SIZE,
): Promise<T[]> => {
  const size = Math.min(100, Math.max(1, pageSize))
  const all: T[] = []
  let page = 1
  let totalPages = 1
  do {
    const res = await fetchPage(`?page=${page}&pageSize=${size}`)
    all.push(...(res.data ?? []))
    const total = res.paging?.total ?? all.length
    totalPages = Math.max(1, Math.ceil(total / size))
    page += 1
  } while (page <= totalPages)
  return all
}

const parentLookupKey = (parentId: NhomHangId | null | undefined): string =>
  parentId == null || parentId === '' ? 'root' : String(parentId)

/** Map (parentId + tên nhóm) → node — dùng để bỏ qua nhóm hàng đã có. */
export const buildNhomHangLookup = (tree: NhomHangCayNode[]): Map<string, NhomHangCayNode> => {
  const map = new Map<string, NhomHangCayNode>()
  const walk = (nodes: NhomHangCayNode[]) => {
    for (const node of nodes) {
      const key = `${parentLookupKey(node.parentId)}::${normKey(node.tenNhom)}`
      if (!map.has(key)) map.set(key, node)
      if (node.children?.length) walk(node.children)
    }
  }
  walk(tree)
  return map
}

export const findNhomHangNode = (
  lookup: Map<string, NhomHangCayNode>,
  parentId: NhomHangId | null | undefined,
  ten: string,
): NhomHangCayNode | undefined => lookup.get(`${parentLookupKey(parentId)}::${normKey(ten)}`)

export const seedResultMessage = (
  label: string,
  created: number,
  skipped: number,
  total: number,
  extra?: string,
): string => {
  const parts: string[] = []
  if (created > 0) parts.push(`tạo mới ${created}`)
  if (skipped > 0) parts.push(`bỏ qua ${skipped} (đã có)`)
  const summary = parts.length ? parts.join(', ') : 'không có thay đổi'
  const base = `${label}: ${summary} / ${total} mục.`
  return extra ? `${base} ${extra}` : base
}

export const khachHangEmailFromMa = (maKhachHang: string): string =>
  `${maKhachHang.toLowerCase().replace(/[^a-z0-9]+/g, '.')}@gmail.com`

export const nhaCungCapEmailFromMa = (maNhaCungCap: string): string =>
  `lienhe@${maNhaCungCap.toLowerCase().replace(/^ncc-/, '').replace(/[^a-z0-9]+/g, '')}.vn`

export const reportProgress = (
  options: TestDataSeedOptions | undefined,
  created: number,
  total: number,
  message: string,
  skipped = 0,
): void => {
  const done = created + skipped
  const progress: TestDataSeedProgress = {
    created: done,
    total,
    level: total > 0 ? Math.min(99, Math.ceil((done / total) * 100)) : 0,
    message,
  }
  options?.onProgress?.(progress)
}

/** Gọi API tuần tự từng bản ghi (giống bấm Lưu form nhiều lần). */
export const runSequential = async (
  total: number,
  runOne: (index: number) => Promise<void>,
  options?: TestDataSeedOptions,
  messagePrefix = 'Đang tạo',
): Promise<number> => {
  for (let i = 1; i <= total; i += 1) {
    await runOne(i)
    reportProgress(options, i, total, `${messagePrefix}… ${i}/${total}`)
  }
  return total
}
